import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { AttachmentPreview } from "../components/AttachmentPreview";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { CATEGORIES, CATEGORY_LABEL, relTime } from "../lib/labels";
import { CategoryBadge } from "../components/Badges";
import RichEditor from "../components/RichEditor";
import { MagnifyingGlass, BookOpen, Plus, Eye, UploadSimple, Paperclip, TrashSimple, CheckSquare, Square, X } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ConfirmDialog } from "../components/ConfirmDialog";

const VISIBILITIES = [
  { v: "public", label: "Public (accessible sans compte)" },
  { v: "authenticated", label: "Connectés (clients + staff)" },
  { v: "internal", label: "Interne (staff uniquement)" },
];

export default function KnowledgeBase() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isStaff = user?.role === "admin" || user?.role === "technicien";
  const isAdmin = user?.role === "admin";
  const [articles, setArticles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "autre", tags: "", visibility: "authenticated" });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (category !== "__all") params.category = category;
    const r = await api.get("/kb/articles", { params });
    setArticles(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resetForm = () => {
    setForm({ title: "", content: "", category: "autre", tags: "", visibility: "authenticated" });
    setPendingFiles([]);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(articles.map((a) => a.id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setConfirmBulkDelete(false);
    if (ids.length === 0) return;
    try {
      const r = await api.post("/kb/articles/bulk-delete", { ids });
      toast.success(`${r.data.soft_deleted} article(s) déplacé(s) vers la corbeille`);
      exitSelectMode();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/kb/articles", {
        title: form.title,
        content: form.content,
        category: form.category,
        visibility: form.visibility,
        is_html: true,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      const artId = r.data.id;
      // Upload attachments in parallel
      if (pendingFiles.length) {
        await Promise.all(pendingFiles.map((f) => {
          const fd = new FormData(); fd.append("file", f);
          return api.post(`/kb/articles/${artId}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }));
      }
      toast.success(`Article publié${pendingFiles.length ? ` (+${pendingFiles.length} PJ)` : ""}`);
      setOpen(false); resetForm();
      // Navigate to the article so user can see it right away
      nav(`/kb/${artId}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <header className="rounded-xl overflow-hidden border">
        <div className="relative p-8 md:p-12 text-white"
          style={{
            background: "linear-gradient(135deg, hsl(222,60%,14%), hsl(222,80%,26%)), url('https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&q=85') center/cover",
            backgroundBlendMode: "overlay",
          }}>
          <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest">
            <BookOpen size={14}/> Base de connaissances
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold mt-2 max-w-2xl">Articles, procédures &amp; solutions rapides</h1>
          <div className="mt-6 max-w-xl relative">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60"/>
            <input
              data-testid="kb-search"
              placeholder="Rechercher un article, un mot-clé…"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="w-full pl-11 pr-4 h-12 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/40"
            />
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setCategory("__all"); setTimeout(load, 0); }}
            data-testid="kb-cat-all"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${category === "__all" ? "bg-primary text-primary-foreground" : "bg-background"}`}>
            Toutes
          </button>
          {CATEGORIES.map((c) => (
            <button key={c}
              onClick={() => { setCategory(c); setTimeout(load, 0); }}
              data-testid={`kb-cat-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${category === c ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
              {CATEGORY_LABEL[c]}
            </button>
          ))}
        </div>
        {isStaff && (
          <div className="flex items-center gap-2 flex-wrap">
            {selectMode ? (
              <>
                <span className="text-sm text-muted-foreground" data-testid="kb-selection-count">
                  {selectedIds.size} sélectionné(s)
                </span>
                <Button variant="outline" size="sm" onClick={selectAllVisible} data-testid="kb-select-all">
                  Tout sélectionner
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={selectedIds.size === 0}
                  onClick={() => setConfirmBulkDelete(true)}
                  data-testid="kb-bulk-delete-btn"
                >
                  <TrashSimple size={14} className="mr-1"/> Supprimer ({selectedIds.size})
                </Button>
                <Button variant="ghost" size="sm" onClick={exitSelectMode} data-testid="kb-select-cancel">
                  <X size={14} className="mr-1"/> Annuler
                </Button>
              </>
            ) : (
              <>
                {isAdmin && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setSelectMode(true)} data-testid="kb-select-mode-btn">
                      <CheckSquare size={14} className="mr-1.5"/> Sélectionner
                    </Button>
                    <Link to="/kb/corbeille">
                      <Button variant="secondary" data-testid="kb-trash-btn">
                        <TrashSimple size={16} className="mr-2"/> Corbeille
                      </Button>
                    </Link>
                  </>
                )}
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="kb-new-btn"><Plus size={16} className="mr-2"/> Nouvel article</Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle className="font-display">Nouvel article</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label>Titre</Label>
                  <Input required data-testid="kb-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Catégorie</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                      <SelectTrigger data-testid="kb-category"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Visibilité</Label>
                    <Select value={form.visibility} onValueChange={(v) => setForm({...form, visibility: v})}>
                      <SelectTrigger data-testid="kb-visibility"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {VISIBILITIES.map((v) => <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tags (virgules)</Label>
                    <Input data-testid="kb-tags" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="wifi, printer…"/>
                  </div>
                </div>
                <div>
                  <Label>Contenu</Label>
                  <div className="mt-1" data-testid="kb-content-wrap">
                    <RichEditor
                      value={form.content}
                      onChange={(v) => setForm({...form, content: v})}
                      minHeight={260}
                      placeholder="Rédigez votre procédure ici — mise en forme, listes, images…"
                    />
                  </div>
                </div>
                <div>
                  <Label>Pièces jointes (optionnel)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input ref={fileRef} type="file" multiple className="hidden"
                      data-testid="kb-file-input"
                      onChange={(e) => setPendingFiles([...pendingFiles, ...Array.from(e.target.files || [])])}/>
                    <Button type="button" variant="secondary" onClick={() => fileRef.current?.click()} data-testid="kb-file-btn">
                      <UploadSimple size={14} className="mr-1.5"/> Ajouter fichier
                    </Button>
                    {pendingFiles.length > 0 && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Paperclip size={12}/>
                        {pendingFiles.length} fichier(s) sélectionné(s)
                        <button type="button" className="ml-2 text-xs underline hover:text-foreground" onClick={() => setPendingFiles([])}>vider</button>
                      </div>
                    )}
                  </div>
                  {pendingFiles.length > 0 && (
                    <ul className="mt-2 text-xs text-muted-foreground space-y-1">
                      {pendingFiles.map((f, i) => <li key={i} className="truncate">· {f.name} <span className="opacity-60">({(f.size/1024).toFixed(1)} Ko)</span></li>)}
                    </ul>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={busy} data-testid="kb-save">{busy ? "Publication…" : "Publier"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
              </>
            )}
          </div>
        )}
      </div>

      <AttachmentPreview file={preview} onClose={() => setPreview(null)} />
      <div className="rounded-xl border bg-card divide-y overflow-hidden" data-testid="kb-list">
        {articles.map((a) => {
          const isSelected = selectedIds.has(a.id);
          const card = (
            <div className={`flex items-center gap-3 px-3 py-2 min-w-0 transition-colors hover:bg-muted/40 ${selectMode && isSelected ? "bg-primary/5" : ""}`}>
              {selectMode && (
                <span className="shrink-0 text-primary">{isSelected ? <CheckSquare size={18} weight="fill"/> : <Square size={18}/>}</span>
              )}
              <BookOpen size={14} className="shrink-0 text-primary"/>
              <span className="font-medium truncate min-w-0 sm:max-w-[35%]">{a.title}</span>
              <span className="hidden md:inline text-sm text-muted-foreground truncate min-w-0 flex-1">
                {(a.content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").slice(0, 200)}
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                <span className="hidden sm:inline-flex"><CategoryBadge value={a.category}/></span>
                {(a.attachments?.length || 0) > 0 && (
                  <button type="button" title={a.attachments.length === 1 ? `Aperçu de ${a.attachments[0].original_filename}` : "Aperçu de la première pièce jointe"}
                    onClick={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      if (selectMode) { return; }
                      const f = a.attachments[0];
                      setPreview({ name: f.original_filename, type: f.content_type, path: `/kb/attachments/${f.id}` });
                    }}
                    className="inline-flex max-w-[160px] items-center gap-1 rounded-md border px-1.5 py-0.5 text-primary hover:bg-primary/10" data-testid={`kb-open-att-${a.id}`}>
                    <Paperclip size={12} className="shrink-0"/>
                    <span className="truncate">{a.attachments.length === 1 ? a.attachments[0].original_filename : `${a.attachments.length} fichiers`}</span>
                  </button>
                )}
                <span className="inline-flex items-center gap-1"><Eye size={12}/> {a.views}</span>
                <span className="hidden lg:inline w-28 truncate text-right" title={a.author_name}>{a.author_name}</span>
                <span className="hidden sm:inline w-24 text-right">{relTime(a.updated_at)}</span>
              </span>
            </div>
          );
          return selectMode ? (
            <button
              key={a.id}
              onClick={() => toggleSelect(a.id)}
              className="block w-full text-left"
              data-testid={`kb-select-${a.id}`}
              aria-pressed={isSelected}
            >
              {card}
            </button>
          ) : (
            <Link key={a.id} to={`/kb/${a.id}`} className="block" data-testid={`kb-article-${a.id}`}>
              {card}
            </Link>
          );
        })}
        {articles.length === 0 && <p className="text-muted-foreground text-center py-10">Aucun article trouvé.</p>}
      </div>

      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        title={`Supprimer ${selectedIds.size} article(s) ?`}
        description={`Les articles seront déplacés vers la corbeille (récupérables 30 jours). Cette action peut être annulée depuis la page Corbeille.`}
        confirmText="Supprimer"
        variant="destructive"
        onConfirm={bulkDelete}
      />
    </div>
  );
}
